import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Tooltip } from 'antd';

import Form from './Form';

type TestType =
  | 'OCP'
  | 'LOGIC'
  | 'SJT'
  | 'MOTIVATIONAL'
  | 'PROFILEMAP'
  | 'OPERATIONAL'
  | 'INTERESTS'
  | 'NEXA';

interface TestsFormProps {
  tests: string[];
  requireTests?: boolean;
  namePrefix?: (string | number)[];
  showNexa?: boolean;
}

export interface TestsFormValues {
  testTypeList: string[];
}

const TestsForm = ({ tests, requireTests, namePrefix = [], showNexa = false }: TestsFormProps) => {
  const form = Form.useFormInstance();
  const testTypeList = Form.useWatch<string[] | undefined>([...namePrefix, 'testTypeList'], form);

  const testsKeyInfoRelation = {
    OCP: {
      name: 'Mapeamento Cultural',
      description: 'Avalia o fit cultural com a empresa.',
      averageTime: 7,
    },
    LOGIC: {
      name: 'Mental',
      description: 'Avalia a velocidade de aprendizagem e o potencial mental.',
      averageTime: 20,
    },
    SJT: { name: 'Social', description: 'Avalia habilidades interpessoais.', averageTime: 11 },
    MOTIVATIONAL: {
      name: 'Motivacional',
      description: 'Avalia postura motivacional frente a dificuldades e incertezas.',
      averageTime: 13,
    },
    PROFILEMAP: {
      name: 'Perfil',
      description: 'Avalia tendências de comportamento, com base no Big Five.',
      averageTime: 8,
    },
    OPERATIONAL: {
      name: 'Raciocínio Simplificado',
      description: 'Avalia a habilidade de resolução de problemas lógicos e análise numérica.',
      averageTime: 9,
    },
    INTERESTS: {
      name: 'Interesses profissionais',
      description: 'Avalia as atividades de maior interesse do candidato.',
      averageTime: 12,
    },
    NEXA: {
      name: 'Potencial de Liderança',
      description: 'Avalia o potencial de liderança, com base no learning agility.',
      averageTime: 12,
    },
  };

  const cleanedTests = tests.filter((test): test is TestType =>
    [
      'OCP',
      'LOGIC',
      'SJT',
      'MOTIVATIONAL',
      'PROFILEMAP',
      'OPERATIONAL',
      'INTERESTS',
      'NEXA',
    ].includes(test)
  );

  return (
    <>
      <Form.Item
        name={[...namePrefix, 'testTypeList']}
        rules={
          requireTests ? [{ min: 1, type: 'array', message: 'Selecione no mínimo um teste' }] : []
        }
      >
        <Checkbox.Group>
          <Form.Row>
            <Form.Col>
              <Checkbox value="OCP">{testsKeyInfoRelation['OCP']['name']}</Checkbox>
              <Tooltip
                title={
                  <span>
                    {testsKeyInfoRelation['OCP']['description']}
                    <br />
                    <strong>Duração média:</strong> {testsKeyInfoRelation['OCP']['averageTime']}{' '}
                    minutos
                  </span>
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Form.Col>
            {showNexa && (
              <Form.Col flex={1}>
                <Checkbox value="NEXA">{testsKeyInfoRelation['NEXA']['name']}</Checkbox>
                <Tooltip
                  title={
                    <span>
                      {testsKeyInfoRelation['NEXA']['description']}
                      <br />
                      <strong>Duração média:</strong> {testsKeyInfoRelation['NEXA']['averageTime']}{' '}
                      minutos
                    </span>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Form.Col>
            )}
          </Form.Row>
          <Form.Row>
            {cleanedTests.map((test: TestType) => (
              <Form.Col key={test}>
                <Checkbox value={test}>{testsKeyInfoRelation[test]['name']}</Checkbox>
                <Tooltip
                  title={
                    <span>
                      {testsKeyInfoRelation[test]['description']}
                      <br />
                      <strong>Duração média:</strong> {testsKeyInfoRelation[test]['averageTime']}{' '}
                      minutos
                    </span>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Form.Col>
            ))}
          </Form.Row>
        </Checkbox.Group>
      </Form.Item>
      {(!requireTests && testTypeList?.length === 0) ||
        (!cleanedTests.every((test) => testTypeList?.includes(test)) && (
          <>
            <Alert
              message={`Lembre-se: para o cálculo do Match com a posição, o candidato precisa responder os testes: ${cleanedTests
                .map((test) => testsKeyInfoRelation[test as TestType]['name'])
                .join(
                  ', '
                )}. Se o candidato já respondeu algum teste, não se preocupe, ele será avisado ao clicar no link.`}
              type="warning"
              showIcon
            />
            <br />
          </>
        ))}
    </>
  );
};

export default TestsForm;
